import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowUp, ArrowDown, GripVertical, Check, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ResultStatusBadge } from './ResultStatusBadge';
import { PlayerLabel } from './PlayerLabel';
import { ResultButtons } from './ResultButtons';
import { VoidResultButton } from './VoidResultButton';
import type { Pairing } from '@swiss-magic/shared-types';
import type { GameConfig } from '@swiss-magic/shared-types';
import { effectiveMatchStatus } from '@swiss-magic/shared-types';

export function PairingRow({
  pairing,
  index,
  roundId,
  roundStatus,
  isDraggable,
  isDragging,
  isFirst,
  isLast,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onSwap,
  onSetResult,
  onConfirmResult,
  onVoidResult,
  onToggleInProgress,
  isPending,
  gameConfig: _gc, // unused; table headers carry color labels at the page level
  resultOptions,
  swapSelection,
  onPlayerSelect,
}: {
  pairing: Pairing;
  index: number;
  roundId: string;
  roundStatus: string;
  isDraggable: boolean;
  isDragging: boolean;
  isFirst: boolean;
  isLast: boolean;
  onDragStart: (index: number) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (index: number) => void;
  onSwap: (sourceIndex: number, targetIndex: number) => void;
  onSetResult: (roundId: string, pairingId: string, result: string) => void;
  onConfirmResult: (roundId: string, pairingId: string) => void;
  onVoidResult?: (roundId: string, pairingId: string) => void;
  onToggleInProgress?: (
    roundId: string,
    pairingId: string,
    started: boolean,
  ) => void;
  isPending: 'result' | 'confirm' | null;
  gameConfig: GameConfig;
  resultOptions: { value: string; label: string }[];
  swapSelection: string | null;
  onPlayerSelect?: (playerId: string) => void;
}) {
  const { t } = useTranslation('director');
  const isBye = !pairing.blackPlayer;
  const showResults = roundStatus === 'published' || roundStatus === 'completed';
  const isEditable = roundStatus === 'published' && !isBye;
  const selectable = roundStatus === 'paired' && !!onPlayerSelect;
  const showVoid =
    !!onVoidResult &&
    roundStatus === 'published' &&
    (pairing.resultStatus === 'pending' || pairing.resultStatus === 'confirmed');
  // Story 081 — in-progress toggle, desktop-only (PairingRow itself
  // is hidden md:block at the parent). Only meaningful before any
  // result has been entered; once a result lands the badge precedence
  // takes over and the button vanishes.
  const showInProgressButton =
    !!onToggleInProgress &&
    !isBye &&
    roundStatus === 'published' &&
    pairing.resultStatus === 'none';
  const isStarted = pairing.startedAt !== null;
  const effectiveStatus = effectiveMatchStatus(pairing);

  return (
    <tr
      data-testid={`pairing-row-${pairing.tableNumber}`}
      className={`border-b ${isDragging ? 'opacity-50' : ''}`}
      draggable={isDraggable}
      onDragStart={() => onDragStart(index)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={() => onDrop(index)}
    >
      {isDraggable && (
        <td className="py-2">
          <div className="flex items-center gap-0.5">
            <GripVertical
              className="h-4 w-4 cursor-grab text-muted-foreground"
              data-testid={`drag-handle-${pairing.tableNumber}`}
            />
            {!isFirst && (
              <button
                className="h-5 w-5 rounded text-muted-foreground hover:text-foreground"
                data-testid={`move-up-${pairing.tableNumber}`}
                onClick={() => onSwap(index, index - 1)}
              >
                <ArrowUp className="h-3 w-3" />
              </button>
            )}
            {!isLast && (
              <button
                className="h-5 w-5 rounded text-muted-foreground hover:text-foreground"
                data-testid={`move-down-${pairing.tableNumber}`}
                onClick={() => onSwap(index, index + 1)}
              >
                <ArrowDown className="h-3 w-3" />
              </button>
            )}
          </div>
        </td>
      )}
      <td className="py-2 font-mono" data-testid={`table-number-${pairing.tableNumber}`}>
        {pairing.tableNumber}
      </td>
      <td className="py-2" data-testid={`white-player-${pairing.tableNumber}`}>
        <PlayerLabel
          player={pairing.whitePlayer}
          selectable={selectable}
          selected={swapSelection === pairing.whitePlayer.id}
          onSelect={onPlayerSelect}
          ratingClass="text-sm text-muted-foreground"
        />
      </td>
      <td className="py-2" data-testid={`black-player-${pairing.tableNumber}`}>
        {isBye ? (
          <span className="italic text-muted-foreground" data-testid="bye-indicator">
            {t('rounds_page.pairing.bye')}
          </span>
        ) : (
          <PlayerLabel
            player={pairing.blackPlayer!}
            selectable={selectable}
            selected={swapSelection === pairing.blackPlayer!.id}
            onSelect={onPlayerSelect}
            ratingClass="text-sm text-muted-foreground"
          />
        )}
      </td>
      {showResults && (
        <td className="py-2">
          <div className="flex items-center justify-center gap-1">
            {isEditable ? (
              <>
                <ResultButtons
                  tableNumber={pairing.tableNumber}
                  currentResult={pairing.result}
                  resultOptions={resultOptions}
                  pendingResult={isPending === 'result'}
                  onSetResult={(result) =>
                    onSetResult(roundId, pairing.id, result)
                  }
                  variant="row"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className={`ml-1 h-7 w-7 p-0 ${
                    isPending === 'confirm'
                      ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500'
                      : pairing.resultStatus === 'pending'
                        ? 'text-green-600 border-green-600 hover:bg-green-50'
                        : 'invisible'
                  }`}
                  data-testid={`confirm-result-${pairing.tableNumber}`}
                  onClick={() => onConfirmResult(roundId, pairing.id)}
                  disabled={pairing.resultStatus !== 'pending'}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <span
                  className="ml-1 w-24 text-xs"
                  data-testid={`result-status-${pairing.tableNumber}`}
                >
                  <ResultStatusBadge status={effectiveStatus} startedAt={pairing.startedAt} />
                </span>
                {showVoid && (
                  <VoidResultButton
                    tableNumber={pairing.tableNumber}
                    onClick={() => onVoidResult!(roundId, pairing.id)}
                    className="ml-1"
                  />
                )}
                {showInProgressButton && (
                  <Button
                    size="sm"
                    variant={isStarted ? 'default' : 'outline'}
                    aria-pressed={isStarted}
                    className="ml-1 h-7 w-7 p-0"
                    data-testid={`in-progress-button-${pairing.tableNumber}`}
                    onClick={() =>
                      onToggleInProgress!(roundId, pairing.id, !isStarted)
                    }
                    title={t(
                      isStarted
                        ? 'rounds_page.in_progress_button.mark_not_started'
                        : 'rounds_page.in_progress_button.mark_started',
                    )}
                  >
                    <Play className="h-3.5 w-3.5" />
                  </Button>
                )}
              </>
            ) : (
              <span data-testid={`result-display-${pairing.tableNumber}`}>
                {isBye ? (
                  <Badge
                    variant="secondary"
                    className="text-xs"
                    data-testid={`result-status-${pairing.tableNumber}`}
                  >
                    {t('rounds_page.pairing.bye_result')}
                  </Badge>
                ) : (
                  <span className="font-mono">
                    {pairing.result || '—'}
                  </span>
                )}
                {isBye && pairing.resultStatus === 'confirmed' && (
                  <span className="sr-only" data-testid={`bye-confirmed-${pairing.tableNumber}`}>
                    {t('rounds_page.pairing.bye_confirmed_sr')}
                  </span>
                )}
              </span>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
